import { Form, Tooltip } from 'antd'
import { Rule } from 'antd/lib/form'
import HelpIcon from '../HelpIcon'
import { useEffect, useState } from 'react'
import './index.css'
import { CIMSFormItemProps } from './types'
import { parseDisabled } from '../../utils/parseDisabled'
import DefaultMessage from '../DefaultMessage'

/**
 * wraps a {@link Form.Item} for use in CIMS
 */
const CIMSFormItem: React.FC<CIMSFormItemProps> = ({
  help,
  label,
  children,
  requiredMessage,
  emailMessage,
  defaultMessage,
  invalid,
  customValidator,
  disabled,
  full = false,
  description,
  url = false,
  ...rest
}) => {
  const [rules, setRules] = useState<Rule[] | undefined>()
  let resolvedDefaultMessage
  if (defaultMessage)
    resolvedDefaultMessage =
      typeof defaultMessage === 'object'
        ? defaultMessage
        : { value: defaultMessage }
  useEffect(() => {
    const rules = Array<Rule>()
    if (!!requiredMessage)
      rules.push({ required: true, message: requiredMessage })
    if (!!emailMessage) rules.push({ type: 'email', message: emailMessage })
    if (invalid) {
      if (Array.isArray(invalid)) {
        invalid.forEach((item) =>
          rules.push({
            pattern: RegExp(item.testPattern),
            message: item.message,
          })
        )
      } else {
        rules.push({
          pattern: RegExp(invalid.testPattern),
          message: invalid.message,
        })
      }
    }
    if (url)
      rules.push({
        type: 'url',
        message:
          'Please enter a valid URL. It may need to be prefixed with http:// or https://',
      })
    if (customValidator) {
      const customValidators = Array.isArray(customValidator)
        ? customValidator
        : [customValidator]
      for (let cv of customValidators) {
        rules.push((formInstance) => ({
          validator: (_, value) => {
            if (cv.validator(value, formInstance)) return Promise.resolve()
            return Promise.reject()
          },
          message: cv.message,
        }))
      }
    }
    setRules(!rules.length ? undefined : rules)
  }, [invalid, requiredMessage, emailMessage, url, customValidator])

  return (
    <Tooltip title={parseDisabled(disabled)}>
      <Form.Item
        {...rest}
        style={full ? { width: '100%' } : undefined}
        help={
          description ??
          (resolvedDefaultMessage ? (
            <DefaultMessage {...resolvedDefaultMessage} />
          ) : undefined)
        }
        label={<HelpIcon text={help}>{label}</HelpIcon>}
        /**
         * Just so you know, the index.css file for this component overwrites
         * the default styling associated with this prop
         */
        colon={!!label}
        rules={rules}
      >
        {children}
      </Form.Item>
    </Tooltip>
  )
}

export default CIMSFormItem
